import * as Icon from "@iyk/icons"
import * as UI from "@iyk/ui"
import * as React from "react"

import useEmblaCarousel from "embla-carousel-react"

import { cva } from "class-variance-authority"

// #region Context

const CarouselContext = React.createContext<CarouselContextType | null>(null)

const useCarousel = () => {
  const context = React.useContext(CarouselContext)
  if (context === null) {
    throw new Error("useCarousel must be used within a Carousel.Root")
  }
  return context
}

// #endregion

// #region Root

const Root = ({
  options,
  children,
  className,
  ...props
}: {
  options?: CarouselOptions
} & JSX.IntrinsicElements["section"]) => {
  const [ref, api] = useEmblaCarousel(options)

  const [selectedSnap, setSelectedSnap] = React.useState(0)
  const [snapCount, setSnapCount] = React.useState(0)

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index)
    },
    [api],
  )

  const onInit = React.useCallback(() => {
    if (!api) {
      return
    }
    setSnapCount(api.scrollSnapList().length)
  }, [api])

  const updateScrollSnapState = React.useCallback(() => {
    if (!api) {
      return
    }
    setSnapCount(api.scrollSnapList().length)
    setSelectedSnap(api.selectedScrollSnap())
  }, [api])

  React.useEffect(() => {
    if (!api) {
      return
    }

    onInit()
    updateScrollSnapState()

    api.on("reInit", onInit)
    api.on("select", updateScrollSnapState).on("reInit", updateScrollSnapState)

    return () => {
      api.off("reInit", onInit)
      api.off("select", updateScrollSnapState).off("reInit", updateScrollSnapState)
    }
  }, [api, onInit, updateScrollSnapState])

  return (
    <CarouselContext.Provider
      value={{ api, ref, scrollPrev, scrollNext, scrollTo, selectedSnap, snapCount }}
    >
      <section className={classForCarouselRoot({ className })} data-role="carousel-root" {...props}>
        {children}
      </section>
    </CarouselContext.Provider>
  )
}

const classForCarouselRoot = cva(["relative"])

// #endregion

// #region Slides

const Slides = ({
  children,
  className,
  ...props
}: Omit<JSX.IntrinsicElements["div"], "className" | "ref"> & {
  className?: {
    root?: string
    viewport?: string
    container?: string
  }
}) => {
  const { ref } = useCarousel()

  return (
    <div
      ref={ref}
      className={classForSlidesViewport({ className: className?.viewport })}
      data-role="carousel-slides-viewport"
      {...props}
    >
      <div
        className={classForSlidesContainer({ className: className?.container })}
        data-role="carousel-slides-container"
      >
        {children}
      </div>
    </div>
  )
}

const classForSlidesViewport = cva(["overflow-hidden"])
const classForSlidesContainer = cva(["flex -ml-10 [touch-action:pan-y_pinch-zoom]"])

// #endregion

// #region Slide

const Slide = ({ children, className, ...props }: JSX.IntrinsicElements["div"]) => {
  return (
    <section className={classForSlidesSlide({ className })} {...props}>
      {children}
    </section>
  )
}

const classForSlidesSlide = cva(["min-w-0 pl-10 select-none", "[transform:translate3d(0,0,0)]"])

// #endregion

// #region Controls

const Controls = ({ className, ...props }: JSX.IntrinsicElements["div"]) => {
  const { scrollPrev, scrollNext, snapCount, selectedSnap } = useCarousel()

  if (snapCount <= 1) {
    return null
  }

  return (
    <div className={classForControls({ className })} data-role="carousel-controls" {...props}>
      <UI.Button variant="ghost" onClick={scrollPrev} aria-label="goto previous item">
        <Icon.ArrowLeft />
      </UI.Button>
      <p className="text-xs-upper text-center text-gray-11 w-9">
        {selectedSnap + 1} / {snapCount}
      </p>
      <UI.Button variant="ghost" onClick={scrollNext} aria-label="goto next item">
        <Icon.ArrowRight />
      </UI.Button>
    </div>
  )
}

const classForControls = cva(["flex items-center [&>*]:text-gray-12"])

// #endregion

// #region Types

type CarouselContextType = {
  api: ReturnType<typeof useEmblaCarousel>[1]
  ref: ReturnType<typeof useEmblaCarousel>[0]
  scrollPrev: () => void
  scrollNext: () => void
  scrollTo: (index: number) => void
  selectedSnap: number
  snapCount: number
}

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
export type CarouselOptions = UseCarouselParameters[0]

// #endregion

// #region Export

export { Controls, Root, Slide, Slides, useCarousel }

// #endregion
